import {
  FaCheckCircle,
  FaClipboardCheck,
  FaFileInvoiceDollar,
  FaRegClock,
  FaShieldAlt,
  FaTasks,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";
import ServiceCTASection from "../../components/ServiceCTASection";

const seo = {
  title: "Medical Account Receivables Services | Physicians Success Partners",
  description:
    "Recover outstanding balances faster with PSP’s AR services, reduce aging claims and ensure timely, accurate revenue collection for your practice.",
  keywords:
    "accounts receivable service, AR follow-up, aging claims, collections support",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Accounts Receivable (AR) Management Services",
      url: "https://psprcm.us/services/account-receivables/",
      description:
        "Recover outstanding balances faster with PSP’s AR services, reduce aging claims and ensure timely, accurate revenue collection for your practice.",
      serviceType: "Accounts Receivable Management",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Accounts Receivable Solutions",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "AR Follow-Up Services" },
          },
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Aging Report Analysis" },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Insurance Claim Resolution",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Payment Recovery & Collections",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Denial Follow-Up & Reprocessing",
            },
          },
        ],
      },
    },
    createBreadcrumbSchema(
      "/services/account-receivables",
      "Account Receivables Solutions",
    ),
  ],
};

export default function AccountsReceivables() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/account-receivables"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
          heading: "Medical Account Receivables Services",
          paragraph:
            "Physicians Success Partners (PSP) delivers complete medical accounts receivable services, partnering closely with healthcare providers to recover outstanding balances, reduce aging claims, and ensure every dollar your practice earns is collected efficiently and on time.",
          image: "/img/services-img/account-receivable.webp",
        }}
        grid={{
          kicker: "Cash Flow Support",
          heading:
            "Medical Accounts Receivable Services That Recover Your Revenue Faster",
          description: `At Physicians Success Partners (PSP), our medical accounts receivable services are built around identifying, pursuing, and recovering every outstanding balance before it ages beyond recovery. Our certified AR recovery specialist team brings deep expertise across all major payers, denial categories, and aging bucket management strategies. 
          We manage every step of the accounts receivable management process, from identifying unpaid claims and aging balances to systematic follow-up, appeal filing, and final payment posting, so your practice maintains healthy cash flow and minimal revenue leakage at all times.
            `,
          metrics: [
            {
              icon: <FaFileInvoiceDollar />,
              value: "20+",
              label: "Years of Combined AR Management Experience",
              style: "metricPrimary",
            },
            {
              icon: <FaRegClock />,
              value: "95%",
              label: "AR Recovery Success Rate",
              style: "metricAccent",
            },
            {
              icon: <FaClipboardCheck />,
              value: "30 Days",
              label: "Average AR Resolution Time",
              style: "metricLight",
            },
            {
              icon: <FaShieldAlt />,
              value: "100%",
              label: "HIPAA Compliance",
              style: "metricDark",
            },
          ],
          panelEyebrow: "Receivables Control",
          panelHeading:
            "Optimized for Consistent and Accurate AR Management Outcomes at Every Stage",
          assurances: [
            {
              icon: <FaTasks />,
              label: "Accurate Aging Claim Identification & Prioritization",
            },
            {
              icon: <FaCheckCircle />,
              label: "Reliable Payer Follow-Up & Balance Recovery ",
            },
            {
              icon: <FaRegClock />,
              label: "Timely Payment Posting & Reconciliation",
            },
          ],
        }}
        services={{
          heading:
            "PSP's Top Rated Medical Accounts Receivable Services Group Is Here for Your Practice",
          description:
            "PSP's AR recovery specialist team manages your entire accounts receivable lifecycle from unpaid claim identification to final balance resolution. We identify aging patterns, prevent revenue loss, and implement best practices that ensure every outstanding claim is pursued systematically and recovered efficiently. Our structured revenue cycle AR services approach helps practices of all sizes reduce their AR days, improve cash flow, and eliminate the revenue leakage that unmanaged receivables create month after month.",
          items: [
            {
              title: "Systematic Aging Claim Follow-Up",
              text: "Our medical accounts receivable services team works every aging bucket from 30 to 120+ days with structured follow-up workflows, payer-specific escalation strategies, and persistent claim pursuit so no outstanding balance is overlooked or written off prematurely.",
            },
            {
              title: "Steadier Cash Flow Performance",
              text: "Our AR recovery specialist team supports consistent revenue collection by maintaining real-time visibility into every open claim, tracking payer response timelines, and escalating unresolved balances before they move into uncollectable territory.",
            },
            {
              title: "Reduced Administrative Burden",
              text: "By managing complex accounts receivable management externally, we free your internal staff from manually chasing unpaid claims, tracking aging reports, and managing payer correspondence, so your team stays focused entirely on patient care and practice growth.",
            },
            {
              title: "Compliance-Minded AR Execution",
              text: "We stay fully aligned with payer contracts, timely filing limits, and healthcare billing regulations so your revenue cycle AR services process remains dependable, organized, audit-ready, and fully compliant at every stage of recovery.",
            },
          ],
        }}
        faq={{
          image: "/img/services-img/account-receivable.webp",
          imageAlt: "Accounts receivable support",
          title:
            "Hire a Dedicated AR Recovery Specialist to Protect Your Practice's Revenue Cycle",
          description:
            "Our accounts receivable teams are more than just follow-up callers. We are every USA provider's most trusted medical accounts receivable services partner. Our outstanding claims recovery advocates work alongside physicians, group practices, and healthcare organizations to systematically pursue every unpaid balance, resolve denial-related AR, and ensure no earned revenue is left uncollected.",
          btnText: "Let's Connect",
        }}
        specialtiesContent={{
          eyebrow: "Collections Support",
          heading:
            "Accounts receivable workflows built for better follow-through and stronger cash flow.",
          description:
            "We help specialty practices keep aging receivables visible, organized, and actively worked toward resolution.",
        }}
      />
      <ServiceCTASection
        imageSrc="/img/services-img/cta-section-img.webp"
        title="Dedicated AR Recovery Specialist for Your Clinic's Revenue Cycle"
        description="Unmanaged medical accounts receivable services gaps can silently drain your practice's revenue month after month, turning collectible claims into permanent write-offs that directly reduce your annual income. PSP's certified AR recovery specialist team streamlines your entire accounts receivable management process, from aging claim identification and payer follow-up to denial appeals, payment posting, and trend-based prevention strategies, all supported by real-time AR dashboards and detailed monthly reporting so you maintain full visibility and control over every outstanding balance at all times."
        btnText="Let's Connect"
      />
    </>
  );
}
